import React from 'react';
import { Typography, Upload, Radio } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { ActionButton } from '@doer/frontend-components/buttons';
import { Spinner } from '@doer/frontend-components/spinner';
import { FormInput } from '../../ui/inputs/form-input';
import { CustomTooltip } from './custom-tooltip';
import { useImageUpload } from '../../../hooks/media/use-image-upload';
import type { BaseFieldProps } from '../../../types/form-renderer';
import type { ImageField as ImageFieldType } from '../../../types/templates';

const { Text } = Typography;

type Props = BaseFieldProps<ImageFieldType>;

export const ImageField: React.FC<Props> = ({ field, value, onChange, commonStyle, labelStyle, error }) => {
  const [imageInputType, setImageInputType] = React.useState<'link' | 'upload'>('link');
  const { uploadImage, isUploading } = useImageUpload();
  const [uploadedFileName, setUploadedFileName] = React.useState<string | null>(null);

  const handleUpload = async (file: File) => {
    const url = await uploadImage(file);
    if (url) {
      onChange(url);
      setUploadedFileName(file.name);
    }
  };

  return (
    <div style={commonStyle}>
      <Text type='secondary' style={{ ...labelStyle, textTransform: 'uppercase' }}>
        {field.label}
      </Text>

      <div style={{ marginBottom: 12 }}>
        <Radio.Group
          value={imageInputType}
          onChange={e => {
            setImageInputType(e.target.value);
            if (e.target.value === 'link') setUploadedFileName(null);
          }}
          style={{ display: 'flex', gap: 8 }}
        >
          <Radio value='link' style={{ fontSize: commonStyle.fontSize }}>
            URL
          </Radio>
          <Radio value='upload' style={{ fontSize: commonStyle.fontSize }}>
            Subir archivo
          </Radio>
        </Radio.Group>
      </div>

      {imageInputType === 'link' ? (
        <CustomTooltip field={field}>
          <FormInput
            size='S'
            placeholder='https://ejemplo.com/imagen.jpg'
            value={value || ''}
            onChange={e => onChange(e.target.value)}
            status={error ? 'error' : undefined}
          />
        </CustomTooltip>
      ) : (
        <CustomTooltip field={field}>
          <Upload
            accept='image/jpeg,image/jpg,image/png'
            beforeUpload={file => {
              handleUpload(file);
              return false;
            }}
            showUploadList={false}
          >
            <ActionButton variant='outline' size='S' disabled={isUploading}>
              {isUploading ? <Spinner size={14} /> : <UploadOutlined />}
              {isUploading
                ? 'Subiendo...'
                : uploadedFileName
                  ? uploadedFileName.length > 20
                    ? `${uploadedFileName.substring(0, 20)}...`
                    : uploadedFileName
                  : 'Seleccionar imagen JPG/PNG'}
            </ActionButton>
          </Upload>
        </CustomTooltip>
      )}

      {field.helperText && (
        <Text type='secondary' style={{ fontSize: 10, marginTop: 4, display: 'block' }}>
          {field.helperText.join(' • ')}
        </Text>
      )}

      {error && (
        <Text type='danger' style={{ fontSize: 12, marginTop: 4 }}>
          {error}
        </Text>
      )}
    </div>
  );
};
